import { useState, useEffect } from 'react';
import { FaLeaf, FaUsers, FaAward, FaClock } from 'react-icons/fa';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';

const About = () => {
  const [aboutImages, setAboutImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAboutImages = async () => {
      try {
        // Query for active about section images (up to 4)
        const imagesRef = collection(db, 'gallery_images');
        const q = query(
          imagesRef,
          where('isActive', '==', true),
          where('landingSection', '==', 'about'),
          limit(4)
        );
        
        const querySnapshot = await getDocs(q);
        const imagesList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          imagesList.push({
            id: doc.id,
            url: data.url,
            alt: data.alt || data.title || 'Landscaping work',
            title: data.title
          });
        });
        
        setAboutImages(imagesList);
      } catch (err) {
        console.error('Error loading about images:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAboutImages();
  }, []);

  // Fallback images for when no images are assigned
  const fallbackImages = [
    {
      id: 'fallback-1',
      url: "/fallback1.webp",
      alt: "Field with landscaping work",
      title: "Field Mowing"
    },
    {
      id: 'fallback-2',
      url: "/fallback2.webp",
      alt: "Snow plowing service",
      title: "Snow Removal"
    },
    {
      id: 'fallback-3',
      url: "/fallback3.webp",
      alt: "Field with landscaping work",
      title: "Field Mowing"
    },
    {
      id: 'fallback-4',
      url: "/fallback4.webp",
      alt: "Garden design example",
      title: "Garden Design"
    }
  ];

  // Use Firebase images if available, otherwise use fallbacks
  const displayImages = aboutImages.length > 0 ? aboutImages : fallbackImages;

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Winnepocket Landscaping
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              With over 30 years of experience transforming and maintaining outdoor spaces throughout Central NH, we bring expertise, creativity and passion to every project.
              As a family owned business we pride ourselves on attention to detail, reliability and exceptional customer service from initial consultation to project completion, be it install or full service lawn care maintenance. 
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <div className="text-3xl font-bold text-primary-600 mb-2">1000+</div>
                <div className="text-gray-600">Projects Completed</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <div className="text-3xl font-bold text-primary-600 mb-2">25+</div>
                <div className="text-gray-600">Years Experience</div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <FaLeaf className="w-5 h-5 text-primary-600" />
                </div>
                <span className="text-gray-700 font-medium">Eco-Friendly Practices</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <FaUsers className="w-5 h-5 text-primary-600" />
                </div>
                <span className="text-gray-700 font-medium">Expert Team</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <FaAward className="w-5 h-5 text-primary-600" />
                </div>
                <span className="text-gray-700 font-medium">Quality Guaranteed</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <FaClock className="w-5 h-5 text-primary-600" />
                </div>
                <span className="text-gray-700 font-medium">Timely Delivery</span>
              </div>
            </div>
          </div>

          {/* Right Images Grid */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            )}
            
            {aboutImages.length === 0 && !loading && (
              <div className="absolute top-4 right-4 bg-yellow-100 border border-yellow-300 rounded-lg p-2 z-10">
                <p className="text-xs text-yellow-800">Using placeholder images</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {displayImages.map((image, index) => (
                <div 
                  key={image.id}
                  className={`overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow ${
                    index === 0 ? 'row-span-2' : ''
                  }`}
                >
                  <img 
                    src={image.url} 
                    alt={image.alt}
                    className={`w-full object-cover hover:scale-105 transition-transform duration-300 ${
                      index === 0 ? 'h-full' : 'h-48'
                    }`}
                    onError={(e) => {
                      // If Firebase image fails, use fallback
                      const fallback = fallbackImages[index];
                      if (fallback && e.target.src !== fallback.url) {
                        e.target.src = fallback.url;
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;